/**
 * Recursively disposes every geometry/material found under `root` — shared
 * teardown for any scene lifecycle that owns a subtree of dynamically-loaded
 * meshes (`/fly`'s flight scene, `/assets`' World viewer 3D pane). A `Set`
 * de-duplicates before disposing, since `three/world-instancer.ts` shares
 * one material across every `InstancedMesh` it builds.
 *
 * Note for callers that swap content under one long-lived `WebGPURenderer`:
 * disposing a material is not free bookkeeping. `RenderObject` listens for a
 * material's `dispose` event and tears its render object down — which frees
 * that object's bindings and uniform buffers (wanted) but also evicts its
 * pipeline-cache entry (usually not). Call this on a subtree you are really
 * finished with, not on one you intend to show again; for the latter see
 * `assets/world-scene.ts`'s `regionCache`.
 */

import type * as THREE from "three/webgpu";

export function disposeTree(root: THREE.Object3D): void {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  root.traverse((node) => {
    const drawable = node as Partial<THREE.Mesh>;
    if (drawable.geometry) geometries.add(drawable.geometry);
    if (drawable.material) {
      for (const material of Array.isArray(drawable.material) ? drawable.material : [drawable.material]) {
        materials.add(material);
      }
    }
  });
  for (const geometry of geometries) geometry.dispose();
  for (const material of materials) material.dispose();
}
